import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_THEME = {
  display_name: null,
  primary_color: "#0A0A0A",
  accent_color: "#064E3B",
  footer_text: null,
  logo_data_url: null,
};

const ThemeCtx = createContext({
  theme: DEFAULT_THEME,
  brokerageName: "CORE Realty",
  isAdmin: false,
  refresh: async () => {},
});

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [brokerageName, setBrokerageName] = useState("CORE Realty");
  const [isAdmin, setIsAdmin] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setTheme(DEFAULT_THEME);
      setBrokerageName("CORE Realty");
      setIsAdmin(false);
      return;
    }
    try {
      const res = await api.get("/brokerage/theme");
      setTheme({ ...DEFAULT_THEME, ...(res.data.theme || {}) });
      setBrokerageName(res.data.theme?.display_name || res.data.brokerage_name || "CORE Realty");
      setIsAdmin(!!res.data.is_admin);
    } catch (err) {
      // Theme is non-critical: keep the previous theme on failure and surface nothing to the user.
      void err;
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Apply primary color as a CSS variable for the whole app.
  useEffect(() => {
    if (theme.primary_color) {
      document.documentElement.style.setProperty("--brokerage-primary", theme.primary_color);
    }
    if (theme.accent_color) {
      document.documentElement.style.setProperty("--brokerage-accent", theme.accent_color);
    }
  }, [theme.primary_color, theme.accent_color]);

  const value = useMemo(
    () => ({ theme, brokerageName, isAdmin, refresh }),
    [theme, brokerageName, isAdmin, refresh],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
